import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';

import { Heading } from 'src/components/atoms/heading';
import { Image } from 'src/components/atoms/image';
import { TextLink } from 'src/components/atoms/text-link';

import logo from 'src/assets/images/logo.png';

import { Button } from 'src/components/atoms/button';
import { useLocation, useNavigate } from 'react-router-dom';

import { MenuContext, MenuContextItem } from 'src/components/atoms/menu-context';
import { loadFromCookies, removeFromCookies, removeSessionStorage, clearLocalStorageExcept } from 'src/libs/utils';

import PATHES from 'src/constants/pathes';
import { DOCTOR_ROLE_ID, PHARMACY_ROLE_ID, STORAGE_KEYS } from 'src/constants/constants';
import { useGetInfoUserSuccessLogin, usePostLogOut } from 'src/api/user';
import { Loading } from 'src/components/atoms/loading';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';

import { removeUserInfo, setUserInfo } from 'src/redux/features/login-slice';
import { Notification, NotificationItem } from 'src/components/molecules/notification';
import { useGetDashBoard } from 'src/api/dashboard';
import { NotifyToast, Toaster } from 'src/components/atoms/action-toast';
import { ConfirmAlert } from '../confirm-alert';
import { persistor } from 'src/redux/store';
import { SelectLanguage } from 'src/components/atoms/select-language';
import { useTranslation } from 'react-i18next';
import { useGetOrderId } from 'src/api/order';
import { OrderDetailIdRequest } from 'src/api/models/order';

import { setReset as ResetHealthCare } from 'src/redux/features/health-care-slice';
import { setReset as ResetDiagnosticTest } from 'src/redux/features/diagnostic-test-slice';
import { setReset as ResetImageAnalysation } from 'src/redux/features/image-analysation-slice';
import { setClear } from 'src/redux/features/patient-information-slice';
import { STATUS } from 'src/constants/constants';

interface HeaderTopProps {
  showLogo?: boolean;
}

const buildOrderReminderIds = (orders: OrderDetailIdRequest[]) => {
  const today = dayjs().startOf('day');
  const ids: string[] = [];

  orders.forEach(order => {
    if (!order?.dateTest) return;
    if (order.status === STATUS[1] || order.status === STATUS[2]) return;
    const diffDays = dayjs(order.dateTest).startOf('day').diff(today, 'day');
    if (diffDays !== 1 && diffDays !== 7) return;
    ids.push(`${order.orderId}_${diffDays === 1 ? 'ONE_DAY' : 'ONE_WEEK'}`);
  });

  return Array.from(new Set(ids));
};

export const HeaderTop: React.FC<HeaderTopProps> = ({ showLogo = true }) => {
  const STORAGE_REMINDER_READ_PREFIX = 'RHC_ORDER_REMINDER_READ_';
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { userInfo } = useAppSelector(state => state.authSlice);
  const fullName = userInfo?.fullName;
  const userId = userInfo?.id;
  const accessToken = loadFromCookies('access_token', true);
  const [invitationNumber, setInvitationNumber] = useState(0);
  const [readReminderIds, setReadReminderIds] = useState<string[]>([]);
  const [isLoadingOrderNotifications, setIsLoadingOrderNotifications] = useState(false);
  const [isOpenedSubmitModal, setIsOpenedSubmitModal] = useState(false);
  const [showLanguage, setShowLanguage] = useState(true);
  const { t } = useTranslation();
  const location = useLocation();
  const now = dayjs();
  const currentMonth = now.month() + 1;
  const currentYear = now.year();
  const nextMonthDate = now.add(1, 'month');
  const nextMonth = nextMonthDate.month() + 1;
  const nextYear = nextMonthDate.year();
  const getOrdersCurrentMonth = useGetOrderId(+(userInfo?.userId || 0), currentMonth, currentYear, {
    query: { enabled: false },
  });
  const getOrdersNextMonth = useGetOrderId(+(userInfo?.userId || 0), nextMonth, nextYear, {
    query: { enabled: false },
  });

  const readReminderStorageKey = `${STORAGE_REMINDER_READ_PREFIX}${userInfo?.userId || ''}`;
  const getDashBoard = useGetDashBoard({
    mutation: {
      useErrorBoundary: false,
      onSuccess: ({
        data: {
          data: { countInvites },
        },
      }) => {
        setInvitationNumber(countInvites);
      },
      onError: () => {
        NotifyToast(<Toaster>{t('MESSAGE_CALL_API_FAIL')}</Toaster>, 'error');
      },
    },
  });

  const handleGetDashBoard = useCallback(async (userId: number) => {
    if (getDashBoard.isLoading) return;

    getDashBoard.mutateAsync({ userId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userInfo?.userId) handleGetDashBoard(userInfo.userId);
  }, [handleGetDashBoard, userInfo]);

  useEffect(() => {
    if (!userInfo?.userId) {
      setReadReminderIds([]);
      return;
    }
    try {
      const stored = localStorage.getItem(readReminderStorageKey);
      setReadReminderIds(stored ? JSON.parse(stored) : []);
    } catch {
      setReadReminderIds([]);
    }
  }, [readReminderStorageKey, userInfo?.userId]);

  const orderReminders = useMemo(() => {
    const allOrders: OrderDetailIdRequest[] = [
      ...(getOrdersCurrentMonth.data?.data?.orders || []),
      ...(getOrdersNextMonth.data?.data?.orders || []),
    ];
    const today = dayjs().startOf('day');
    const reminderMap = new Map<
      string,
      {
        id: string;
        orderId: number;
        dateTest: string;
        type: 'ONE_DAY' | 'ONE_WEEK';
        message: string;
        queryDate: string;
      }
    >();

    allOrders.forEach(order => {
      if (!order?.dateTest) return;
      if (order.status === STATUS[1] || order.status === STATUS[2]) return;

      const orderDate = dayjs(order.dateTest).startOf('day');
      const diffDays = orderDate.diff(today, 'day');
      if (diffDays !== 1 && diffDays !== 7) return;

      const reminderType = diffDays === 1 ? 'ONE_DAY' : 'ONE_WEEK';
      const reminderId = `${order.orderId}_${reminderType}`;
      const dateLabel = dayjs(order.dateTest).format('DD/MM/YYYY');
      const timeLabel = dayjs(order.dateTest).format('HH:mm');
      const typeLabel = order.type || '';
      const beforeLabel =
        diffDays === 1 ? t('home.remind-before-one-day') : t('home.remind-before-one-week');
      const message = t('home.order-reminder', {
        type: typeLabel,
        time: timeLabel,
        date: dateLabel,
        before: beforeLabel,
        interpolation: { escapeValue: false },
      });
      reminderMap.set(reminderId, {
        id: reminderId,
        orderId: order.orderId,
        dateTest: order.dateTest,
        type: reminderType,
        message,
        queryDate: dateLabel,
      });
    });

    return Array.from(reminderMap.values()).sort((a, b) => dayjs(a.dateTest).valueOf() - dayjs(b.dateTest).valueOf());
  }, [getOrdersCurrentMonth.data?.data?.orders, getOrdersNextMonth.data?.data?.orders, t]);

  const unreadReminderCount = useMemo(
    () => orderReminders.filter(item => !readReminderIds.includes(item.id)).length,
    [orderReminders, readReminderIds]
  );

  const hasAnyNotification = invitationNumber > 0 || orderReminders.length > 0;
  const hasUnreadNotification = invitationNumber > 0 || unreadReminderCount > 0;

  const handleOpenNotification = useCallback(async () => {
    if (!userInfo?.userId) return;
    setIsLoadingOrderNotifications(true);
    try {
      const [currentMonthResult, nextMonthResult] = await Promise.all([
        getOrdersCurrentMonth.refetch(),
        getOrdersNextMonth.refetch(),
      ]);
      const latestOrders: OrderDetailIdRequest[] = [
        ...(currentMonthResult.data?.data?.orders || []),
        ...(nextMonthResult.data?.data?.orders || []),
      ];
      const allIds = buildOrderReminderIds(latestOrders);
      if (!allIds.length) return;

      setReadReminderIds(allIds);
      localStorage.setItem(readReminderStorageKey, JSON.stringify(allIds));
    } finally {
      setIsLoadingOrderNotifications(false);
    }
  }, [
    getOrdersCurrentMonth,
    getOrdersNextMonth,
    readReminderStorageKey,
    userInfo?.userId,
  ]);

  const getInfoUserSuccessLogin = useGetInfoUserSuccessLogin({
    mutation: {
      useErrorBoundary: false,
      onSuccess: ({ data: { data } }) => {
        const userInformation = {
          ...userInfo,
          ...data,
          dateOfBirth: data?.dateOfBirthday,
        };

        dispatch(setUserInfo(userInformation));
      },
    },
  });

  const handleGetInfoUserSuccessLogin = useCallback(
    async (params: number) => {
      if (getInfoUserSuccessLogin.isLoading) return;

      await getInfoUserSuccessLogin.mutateAsync({ params });
    },
    [getInfoUserSuccessLogin]
  );

  useEffect(() => {
    // const userId = localStorage.getItem('RHC_userId');
    if (userId) {
      const fetchData = async () => await handleGetInfoUserSuccessLogin(+userId);
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Only remove user info if both token is missing AND user info exists
    // This prevents clearing user data during token refresh
    if (!accessToken && userInfo?.fullName) {
      // Add a small delay to prevent race conditions with token refresh
      const timeoutId = setTimeout(() => {
        const reCheckToken = loadFromCookies('access_token', true);
        if (!reCheckToken) {
          dispatch(removeUserInfo());
          removeSessionStorage(STORAGE_KEYS.USER_ID);
        }
      }, 200);

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const resetData = async () => {
    dispatch(setClear());
    dispatch(removeUserInfo());
    removeFromCookies('access_token');
    persistor.pause();
    await persistor.purge();
    clearLocalStorageExcept([STORAGE_KEYS.DEVICE_ID]);
    setIsOpenedSubmitModal(false);
    navigate(PATHES.HOME_PAGE);
  };

  const postLogOut = usePostLogOut({
    mutation: {
      useErrorBoundary: false,
      onSuccess: () => resetData(),
      onError: () => resetData(),
    },
  });

  const handlePostLogOut = useCallback(
    async () => {
      if (postLogOut.isLoading) {
        return;
      }

      await postLogOut.mutateAsync();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [postLogOut.isLoading, postLogOut.isSuccess]
  );

  const resetDataRedux = () => {

    //health-care-slice
    dispatch(ResetHealthCare());
    //diagnostic-test-slice
    dispatch(ResetDiagnosticTest());
    //image-analysisation-slice
    dispatch(ResetImageAnalysation());
  };
  useEffect(() => {
    const pathHideLanguage = [
      '/healthcare/confirm/',
      '/healthcare/complete/',
      '/healthcareonline/confirm/',
      '/healthcareonline/complete/',
      '/diagnostictest/confirm/',
      '/diagnostictest/complete/',
      '/imageanalysation/confirm/',
      '/imageanalysation/complete/',
    ];
    if (pathHideLanguage.includes(location.pathname)) {
      setShowLanguage(true); //namnh: pending disable language in these screen.
    } else {
      setShowLanguage(true);
    }
  }, [location]);
  return (
    <>
      <header className="o-header-top">
        <div className="o-header-top__content container" style={{ maxWidth: '1170px !important', paddingRight: '15px !important', paddingLeft: '15px !important', marginLeft: 'auto !important', marginRight: 'auto !important' }}>
          {fullName ? (
            <div className="o-header-top__user">
              <Notification hasNotification={hasUnreadNotification} onOpen={handleOpenNotification}>
                {invitationNumber > 0 && (
                  <NotificationItem>
                    {`${t('home.you-have')} ${invitationNumber} ${t('home.tracking-request-unanswered')}.`}
                    <TextLink href={`${PATHES['RELATIVE_MANAGEMENT_PAGE']}?type=watched`}>
                      {t('home.check-now')}
                    </TextLink>
                  </NotificationItem>
                )}
                {isLoadingOrderNotifications && (
                  <NotificationItem className="m-notification__item--loading">
                    <Loading open inblock size="extra-small" />
                  </NotificationItem>
                )}
                {orderReminders.map(reminder => (
                  <NotificationItem key={reminder.id}>
                    {reminder.message}{' '}
                    <TextLink
                      href={`${PATHES['DETAIL_APPLICATION_LIST_PAGE']}?queryDate=${reminder.queryDate}`}
                    >
                      {t('home.check-now')}
                    </TextLink>
                  </NotificationItem>
                ))}
                {!isLoadingOrderNotifications && !hasAnyNotification && (
                  <NotificationItem>{t('home.no-new-notification')}</NotificationItem>
                )}
              </Notification>
              <MenuContext title={fullName} avatarImg={userInfo.urlImage}>
                <MenuContextItem href={PATHES['INFO_USER_PAGE']}>
                  <span className="o-header-top__index">I.</span>
                  {String(t('menu.personal-information'))}
                </MenuContextItem>
                <MenuContextItem href={PATHES['RELATIVE_MANAGEMENT_PAGE']}>
                  <span className="o-header-top__index">II.</span>
                  {String(t('menu.list-of-patients-or-relatives'))}
                </MenuContextItem>
                <MenuContextItem href={PATHES['PATIENT_INFORMATION_PAGE']}>
                  <span className="o-header-top__index">III.</span>
                  {String(t('menu.patient-or-relative-information'))}
                </MenuContextItem>
                <MenuContextItem href={PATHES['HEALTH_MONITORING_PAGE']}>
                  <span className="o-header-top__index">IV.</span>
                  {String(t('menu.health-tracking'))}
                </MenuContextItem>
                <MenuContextItem href={PATHES['DETAIL_APPLICATION_LIST_PAGE']}>
                  <span className="o-header-top__index">V.</span>
                  {String(t('menu.manage-registration-schedule'))}
                </MenuContextItem>
                {userInfo?.jobId === DOCTOR_ROLE_ID && (
                  <MenuContextItem href={PATHES['DETAIL_SCHEDULE_MANAGEMENT_PAGE']}>
                    <span className="o-header-top__index">VI.</span>
                    {String(t('menu.doctors-schedule'))}
                  </MenuContextItem>
                )}
                {userInfo?.jobId === DOCTOR_ROLE_ID && (
                  <MenuContextItem href={PATHES['PRESCRIPTION_MANAGEMENT_PAGE']}>
                    <span className="o-header-top__index">VII.</span>
                    Quản lý toa thuốc
                  </MenuContextItem>
                )}
                {userInfo?.jobId === PHARMACY_ROLE_ID && (
                  <MenuContextItem href={PATHES['PHARMACY_MANAGEMENT_PAGE']}>
                    <span className="o-header-top__index">VI.</span>
                    Quản lý đơn thuốc
                  </MenuContextItem>
                )}
              </MenuContext>

              <span className="o-header-top__span"> | </span>
              <Button modifiers="link" onClick={() => setIsOpenedSubmitModal(true)}>
                {t('exit')}
              </Button>
              {showLanguage && (
                <>
                  <span className="o-header-top__span"> | </span>
                  <div className="o-header-top__language">
                    <SelectLanguage theme="light" />
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="o-header-top__user">
              <TextLink useExternalLink={false} href={PATHES['LOGIN_PAGE']} modifiers="with-textLink">
                {t('signup.login')}
              </TextLink>
              <span className="o-header-top__span"> | </span>
              <TextLink useExternalLink={false} href={PATHES['REGISTER_PAGE']} modifiers="with-textLink">
                {t('signup.register')}
              </TextLink>
              <span className="o-header-top__span"> | </span>
              <div className="o-header-top__language">
                <SelectLanguage theme="light" />
              </div>
            </div>
          )}
        </div>
        {showLogo && (
          <div className="o-header-top__link container">
            <TextLink href={PATHES['HOME_PAGE']} modifiers="none-underline" onClick={resetDataRedux}>
              <div className="o-header-top__logo">
                <Image url={logo} />
                <Heading level="h1">Royal Healthcare</Heading>
              </div>
            </TextLink>
          </div>
        )}

      </header>
      {isOpenedSubmitModal && (
        <ConfirmAlert
          handleCloseRequest={() => setIsOpenedSubmitModal(false)}
          title={t('alert.title-sign-out')}
          cancelButton={
            <Button modifiers="delete" type="button" onClick={() => setIsOpenedSubmitModal(false)}>
              {t('alert.deny')}
            </Button>
          }
          submitButton={
            <Button modifiers="login" type="button" onClick={handlePostLogOut}>
              {t('alert.confirm')}
            </Button>
          }
        >
          {t('alert.messages')}
        </ConfirmAlert>
      )}
      <Loading
        open={
          getInfoUserSuccessLogin.isLoading ||
          getDashBoard.isLoading ||
          postLogOut.isLoading
        }
      />
    </>
  );
};
